export const TODAY_SLUG = 'ff-today';
export const TODAY_CONTACT_PATH = '/event/ff-today-contact';
export const TODAY_SUBMIT_PATH = '/event/ff-today-submit';

/** contact 매물 선택 쿼리 (?branch=songdo) */
export const TODAY_BRANCH_QUERY_KEY = 'branch';

export const TODAY_ASSET_PATHS = {
  contactSlotBody: 'events/ff-today/contact_slot_body.png',
} as const;

export type TodayContactOfferId = 'gangnam' | 'songdo' | 'magok' ;

export type TodayContactOffer = {
  id: TodayContactOfferId;
  /** getPublishingImage 경로 */
  signImage: string;
  signImageMo: string;
  signAlt: string;
  /** 슬롯에 표시할 만원 단위 (예: 18 → 18만원) */
  priceManwon: number;
  updatedHoursAgo: number;
};

/** contact 슬롯 머신 — sign / 가격 / 업데이트 시간이 한 세트 */
export const TODAY_CONTACT_OFFERS: TodayContactOffer[] = [
  {
    id: 'gangnam',
    signImage: 'events/ff-today/slot_sign_gangnam.png',
    signImageMo: 'events/ff-today/slot_sign_gangnam_mo.png',
    signAlt: '강남 6인 사무실',
    priceManwon: 24,
    updatedHoursAgo: 3,
  },
  {
    id: 'songdo',
    signImage: 'events/ff-today/slot_sign_songdo.png',
    signImageMo: 'events/ff-today/slot_sign_songdo_mo.png',
    signAlt: '송도 5인 사무실',
    priceManwon: 18,
    updatedHoursAgo: 4,
  },
  {
    id: 'magok',
    signImage: 'events/ff-today/slot_sign_magok.png',
    signImageMo: 'events/ff-today/slot_sign_magok_mo.png',
    signAlt: '마곡 7인 사무실',
    priceManwon: 23,
    updatedHoursAgo: 6,
  }
];

export function getTodayContactOffer(branch: string | null | undefined): TodayContactOffer {
  const found = TODAY_CONTACT_OFFERS.find((offer) => offer.id === branch);
  return found ?? getDailyContactOffer();
}

export const TODAY_CONTACT_COPY = {
  badge: '지금 보신 특가, 내일은 사라져요!',
  titleHidden: '오늘의 특가',
  slotBodyAlt: '오늘의 특가 슬롯 머신 내부',
  updatedHoursAgo: (hours: number) => `${hours}시간 전 업데이트`,
  priceAriaLabel: (manwon: number) => `${manwon}만원`,
  countdownReset: '이후 가격 리셋! 특가를 빠르게 선점하세요',
  stepSubmitLabel: '오늘의 특가 리스트 확인하기',
  stepIntroMain: '사무실 예상 인원 수를 입력하고,',
  stepIntroSubLine: '오늘의 특가 매물을 확인해 보세요.',
  stepIntroHint: '(예: 5명, 20명 등)',
  stepFooterLines: [
    '*당일 특가를 확인하신 분에 한해 특가 계약이 가능합니다',
    '*마케팅 활용 동의를 하신 분들만 혜택을 받을 수 있어요',
  ] as const,
} as const;

/** submit 비주얼 — 슬롯·타이머는 contact와 같고, 간판·문구만 다름 */
export const TODAY_SUBMIT_COPY = {
  signLabel: '오늘의 특가',
  headline: '패스트파이브 2026년 최저가!',
  countdownNote: <>모든 특가 매물은 <br className="hidden_pc"/>선착순 소진됩니다</>,
  slotBodyAlt: '오늘의 특가 슬롯 머신',
  leverLabel: '다음 지점 특가 보기',
} as const;

/** submit_slot_body(2496×2036) 우측 스트립. 1 대기, 클릭 시 5까지 당겼다 복귀 */
export const TODAY_SUBMIT_LEVER_FRAMES = [1, 2, 3, 4, 5].map(
  (frame) => `events/ff-today/submit_lever${frame}.png`,
);

/** 클릭: 1→5로 내린 뒤 5→1로 복귀 */
export const TODAY_SUBMIT_LEVER_PULL = [1, 2, 3, 4, 5, 4, 3, 2, 1] as const;

export const TODAY_SUBMIT_LEVER_FRAME_MS = 70;

/** 슬롯 릴 — 감속 구간 + 0~9 여러 바퀴 후 목표 숫자에서 멈춤 */
export const SLOT_REEL_DECEL_DIGITS = [4, 3, 2, 1] as const;
export const SLOT_REEL_DIGIT_CYCLES = 2;

/** 칸별 0.5s 간격 순차 시작·정지 (재생 1.5s → 1~4번 각각 1.5s / 2s / 2.5s / 3s에 멈춤) */
export const SLOT_COLUMN_TIMING = {
  tens: { delay: '0s', duration: '1.5s' },
  ones: { delay: '0.5s', duration: '1.5s' },
  man: { delay: '1s', duration: '1.5s' },
  won: { delay: '1.5s', duration: '1.5s' },
} as const;

/** 마지막 릴이 멈춘 뒤 다시 돌리기까지 대기 */
export const SLOT_REPLAY_PAUSE_MS = 10_000;

/** 첫 칸 시작부터 마지막 칸이 멈출 때까지 */
export function getSlotSpinDurationMs() {
  const last = SLOT_COLUMN_TIMING.won;
  return (Number.parseFloat(last.delay) + Number.parseFloat(last.duration)) * 1000;
}

export const SLOT_UNIT_SPIN_PAIRS = 10;

export type SlotReelKey = 'tens' | 'ones';
export type SlotUnitKey = 'man' | 'won';

export type SlotDigitColumn = {
  type: 'digit';
  key: SlotReelKey;
  /** strip[0] = 멈출 숫자, 아래로 갈수록 지나친 숫자 */
  digits: number[];
  travel: number;
  delay: string;
  duration: string;
};

export type SlotUnitColumn = {
  type: 'unit';
  key: SlotUnitKey;
  /** strip[0] = 멈출 글자 (man | won) */
  units: SlotUnitKey[];
  travel: number;
  delay: string;
  duration: string;
};

export type SlotColumnSpec = SlotDigitColumn | SlotUnitColumn;

function buildDigitStrip(target: number) {
  const tail: number[] = [];
  for (let cycle = 0; cycle < SLOT_REEL_DIGIT_CYCLES; cycle += 1) {
    for (let d = 9; d >= 0; d -= 1) tail.push(d);
  }
  tail.push(...SLOT_REEL_DECEL_DIGITS);
  return { digits: [target, ...tail], travel: tail.length };
}

function buildUnitStrip(final: SlotUnitKey) {
  const other: SlotUnitKey = final === 'man' ? 'won' : 'man';
  const tail: SlotUnitKey[] = [];
  for (let i = 0; i < SLOT_UNIT_SPIN_PAIRS; i += 1) {
    tail.push(other, final);
  }
  return { units: [final, ...tail], travel: tail.length };
}

/** 송도 18 → 1 · 8 · 만 · 원 4칸 릴 */
export function getPriceSlotColumns(priceManwon: number): SlotColumnSpec[] {
  const [tens, ones] = String(priceManwon).padStart(2, '0').split('').map(Number);
  const tensStrip = buildDigitStrip(tens);
  const onesStrip = buildDigitStrip(ones);
  const manStrip = buildUnitStrip('man');
  const wonStrip = buildUnitStrip('won');

  return [
    { type: 'digit', key: 'tens', ...tensStrip, ...SLOT_COLUMN_TIMING.tens },
    { type: 'digit', key: 'ones', ...onesStrip, ...SLOT_COLUMN_TIMING.ones },
    { type: 'unit', key: 'man', ...manStrip, ...SLOT_COLUMN_TIMING.man },
    { type: 'unit', key: 'won', ...wonStrip, ...SLOT_COLUMN_TIMING.won },
  ];
}

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_SECONDS = 24 * 60 * 60;
const DAY_MS = DAY_SECONDS * 1000;
/** 이 날(KST)이 송도. 이후 마곡 → 강남 순으로 하루씩 순환하고, 모두에게 같다. */
const OFFER_ROTATION_ANCHOR = '2026-09-28';

function kstDayNumber(now: number) {
  const kst = new Date(now + KST_OFFSET_MS);
  return Math.floor(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate()) / DAY_MS);
}

function getDailyContactOffer(now = Date.now()): TodayContactOffer {
  const [year, month, day] = OFFER_ROTATION_ANCHOR.split('-').map(Number);
  const anchor = Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
  const length = TODAY_CONTACT_OFFERS.length;
  const index = ((kstDayNumber(now) - anchor) % length + length) % length;
  return TODAY_CONTACT_OFFERS[index];
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

export type KstMidnightCountdown = {
  /** HHMMSS 6자리 */
  digits: string;
  label: string;
};

/** 다음 한국 시간 자정까지 남은 시·분·초. 자정에 00:00:00으로 돌아간다. */
export function remainingUntilKstMidnight(now: number): KstMidnightCountdown {
  const kst = new Date(now + KST_OFFSET_MS);
  const elapsed = kst.getUTCHours() * 3600 + kst.getUTCMinutes() * 60 + kst.getUTCSeconds();
  const left = (DAY_SECONDS - elapsed) % DAY_SECONDS;
  const hours = Math.floor(left / 3600);
  const minutes = Math.floor((left % 3600) / 60);
  const seconds = left % 60;

  return {
    digits: `${pad2(hours)}${pad2(minutes)}${pad2(seconds)}`,
    label: `${hours}시 ${minutes}분 ${seconds}초`,
  };
}

/** num_timer 2자리 그룹 3개 (시·분·초) */
export function splitHmsDigitGroups(digits: string): [string, string, string] {
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)];
}

export type TodayFireIcon = 'fire1' | 'fire2';
export type TodayFireClassName = 'fire_1' | 'fire_2' | 'fire_3' | 'fire_4' | 'fire_5' | 'fire_6';

export type TodayFireDeco = {
  key: string;
  icon: TodayFireIcon;
  className: TodayFireClassName;
};

/** contact_bg(390×1034)/contact_bg_mo 배경 좌표 기준 스파크 장식 — 모바일 2개 · 데스크탑 4개 */
export const TODAY_FIRE_DECOS: TodayFireDeco[] = [
  { key: 'mo-1', icon: 'fire1', className: 'fire_1' },
  { key: 'mo-2', icon: 'fire1', className: 'fire_2' },
  { key: 'pc-1', icon: 'fire2', className: 'fire_3' },
  { key: 'pc-2', icon: 'fire1', className: 'fire_4' },
  { key: 'pc-3', icon: 'fire2', className: 'fire_5' },
  { key: 'pc-4', icon: 'fire1', className: 'fire_6' },
];
